<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import QRCode from 'qrcode'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSyncStore } from '@/stores/sync'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'

const route = useRoute()
const authStore = useAuthStore()
const syncStore = useSyncStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()

const incidentId = route.params.id as string

const collapsed = ref(localStorage.getItem(`qr-card-collapsed-${incidentId}`) === 'true')

function toggleCollapsed() {
  collapsed.value = !collapsed.value
  localStorage.setItem(`qr-card-collapsed-${incidentId}`, String(collapsed.value))
}

const svgContent = ref('')
const joinUrl = ref('')
const loading = ref(false)
const error = ref('')

const canGenerate = computed(() => permissionsStore.can('personnel.generate_qr', opRole.value))

async function fetchToken() {
  if (!syncStore.isOnline) {
    error.value = 'Network required to generate QR code.'
    return
  }
  if (!canGenerate.value) return

  loading.value = true
  svgContent.value = ''
  error.value = ''
  joinUrl.value = ''

  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch(`/api/incidents/${incidentId}/join-token`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    const json = await res.json()
    if (!res.ok || json.error) {
      throw new Error(json.error?.message ?? 'Failed to generate token')
    }
    joinUrl.value = `${window.location.origin}${json.data.join_url}`
    svgContent.value = await QRCode.toString(joinUrl.value, { type: 'svg', margin: 2 })
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : 'Failed to generate QR code'
  } finally {
    loading.value = false
  }
}

async function downloadQR() {
  if (!joinUrl.value) return
  const dataUrl = await QRCode.toDataURL(joinUrl.value, { width: 512, margin: 2 })
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = `incident-${incidentId}-checkin-qr.png`
  a.click()
}

watch(canGenerate, (val) => {
  if (val && !svgContent.value && !loading.value) fetchToken()
}, { immediate: false })

onMounted(() => {
  if (canGenerate.value) fetchToken()
})
</script>

<template>
  <div class="bg-surface border border-border rounded-lg p-4">
    <button
      class="flex items-center justify-between w-full mb-3"
      @click="toggleCollapsed"
    >
      <p class="text-[10px] font-label uppercase tracking-widest text-outline">QR Check-In</p>
      <span class="material-symbols-outlined text-[14px] text-outline transition-transform" :class="collapsed ? '' : 'rotate-180'">expand_more</span>
    </button>

    <template v-if="!collapsed">
    <!-- No permission -->
    <div v-if="opRole !== undefined && !canGenerate" class="flex items-center justify-center h-32 text-[11px] text-outline font-mono text-center px-2">
      Not authorized to generate check-in QR.
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="flex items-center justify-center h-40">
      <div class="w-6 h-6 border-2 border-outline/30 border-t-outline animate-spin rounded-full" />
    </div>

    <!-- Offline error -->
    <div v-else-if="error" class="flex flex-col items-center justify-center gap-2 h-40 text-[11px] text-outline font-mono text-center px-2">
      <span class="material-symbols-outlined text-[18px] text-outline/60">wifi_off</span>
      {{ error }}
    </div>

    <!-- QR code -->
    <div v-else-if="svgContent">
      <div v-html="svgContent" class="w-full aspect-square [&>svg]:w-full [&>svg]:h-full bg-white p-2 rounded" />
      <p class="text-[9px] text-outline font-mono uppercase tracking-widest text-center mt-2">Scan to join this incident</p>
    </div>

    <!-- Pending (opRole not yet resolved) -->
    <div v-else class="flex items-center justify-center h-40">
      <div class="w-6 h-6 border-2 border-outline/30 border-t-outline animate-spin rounded-full" />
    </div>

    <!-- Actions -->
    <div v-if="canGenerate" class="flex gap-2 mt-3">
      <button
        class="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-background border border-border rounded text-[11px] text-on-surface-variant hover:border-on-surface/30 hover:text-on-surface disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="loading"
        @click="fetchToken"
      >
        <span class="material-symbols-outlined text-[13px]">refresh</span>
        Regenerate
      </button>
      <button
        class="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-background border border-border rounded text-[11px] text-on-surface-variant hover:border-on-surface/30 hover:text-on-surface disabled:opacity-40 disabled:cursor-not-allowed"
        :disabled="!svgContent || loading"
        @click="downloadQR"
      >
        <span class="material-symbols-outlined text-[13px]">download</span>
        Download
      </button>
    </div>
    </template>
  </div>
</template>
